import { type FormControlProps, FormControl, FormHelperText, InputLabel } from '@mui/material';
import {
  type FieldValues,
  type FieldError,
  type UseControllerProps,
  useController,
} from 'react-hook-form';

import { Flex } from '../../flex';
import { FileInput, MIME_TYPES, type FileInputProps } from '../FileInput';

interface HelperTextProps {
  error?: FieldError;
  helperText?: string | React.ReactElement;
}

function HelperText(props: Readonly<HelperTextProps>) {
  const { error, helperText } = props;

  if (error?.message) {
    return <FormHelperText error>{error?.message}</FormHelperText>;
  }
  if (typeof helperText === 'string') {
    return <FormHelperText>{helperText}</FormHelperText>;
  }
  if (helperText) return helperText;

  return null;
}

export type FormFileInputProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    FormControlProps &
    Omit<FileInputProps, 'value' | 'onDrop'> & {
      onDrop?: FileInputProps['onDrop'];
      label?: string;
      helperText?: string | React.ReactElement;
    };

export default function FormFileInput<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<FormFileInputProps<TFieldValues>>,
) {
  const {
    disabled: isDisabled,
    helperText,
    label,
    multiple: isMultiple,
    name,
    onClear,
    onDrop,
    onReject,
    placeholder,
    required: isRequired,
    ...restProps
  } = props;

  const {
    field: { onChange, onBlur, ref, value, ...field },
    fieldState: { error },
  } = useController({ ...props, name });

  const handleRemove = (files: File[] | null) => {
    if (isMultiple) {
      onChange(files);
    } else {
      onChange(null);
    }
    onBlur();
    onClear?.();
  };

  const handleDrop = (files: File[]) => {
    if (isMultiple) {
      onChange(files);
    } else {
      const file = files[0];
      onChange(file);
    }
    onDrop?.(files);
    onBlur();
  };

  return (
    <Flex>
      <FormControl error={!!error} required={isRequired} disabled={isDisabled} sx={{ width: 1 }}>
        <Flex sx={{ gap: 1, flexDirection: 'column' }}>
          {label ? (
            <InputLabel required={isRequired} sx={{ position: 'unset' }}>
              {label}
            </InputLabel>
          ) : null}
          <FileInput
            {...field}
            {...restProps}
            name={name}
            onDrop={handleDrop}
            onRemove={handleRemove}
            onReject={(fileRejection) => {
              onReject?.(fileRejection);
              onBlur();
            }}
            disabled={isDisabled}
            value={value}
            ref={ref}
            error={!!error}
            multiple={isMultiple}
            placeholder={placeholder}
          />
          <HelperText error={error} helperText={helperText} />
        </Flex>
      </FormControl>
    </Flex>
  );
}

export { MIME_TYPES };
